import { AXIOS_INSTANCE } from '@/api/mutator/custom-instance-new';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { routeLogin } from './constants/authSettings';

/**
 * Auth routes that should not trigger login redirects
 */
const AUTH_ROUTES = ['/auth', '/login', '/register', '/forgot-password', '/reset-password', '/verify-email'] as const;

/**
 * Checks if the current path is an authentication-related route
 */
function isAuthRoute(path: string): boolean {
	return AUTH_ROUTES.some(route => path.includes(route));
}

/**
 * Global auth interceptor that handles 401 responses
 * Should be rendered once at the app level in RootLayout
 */
export function AuthInterceptor() {
	const navigate = useNavigate();
	const interceptorIdRef = useRef<number | null>(null);

	useEffect(() => {
		// Only register if not already registered
		if (interceptorIdRef.current === null) {
			interceptorIdRef.current = AXIOS_INSTANCE.interceptors.response.use(
				response => response,
				error => {
					// Handle 401 errors by redirecting to login
					if (error.response?.status === 401) {
						const currentPath = window.location.pathname + window.location.search;

						// Avoid infinite redirects on auth pages
						if (!isAuthRoute(currentPath)) {
							const redirectPath = encodeURIComponent(currentPath);
							// Navigate to login with return path
							navigate(`${routeLogin}?r=${redirectPath}`, { replace: true });
						} else if (import.meta.env.DEV) {
							// Log auth page 401s in development for debugging
							console.warn('401 received on auth page:', {
								path: currentPath,
								url: error.config?.url,
							});
						}
					}

					return Promise.reject(error);
				},
			);
		}

		// Cleanup function
		return () => {
			if (interceptorIdRef.current !== null) {
				AXIOS_INSTANCE.interceptors.response.eject(interceptorIdRef.current);
				interceptorIdRef.current = null;
			}
		};
	}, [navigate]);

	// Component doesn't render anything - purely for side effects
	return null;
}
